/**
 * Feature Flags Configuration
 * Control feature availability throughout the application
 */

import { env } from './env';

// Feature flags with default values
const defaultFeatures = {
  // UI Features
  darkMode: false,
  animations: true,
  advancedEditor: true,
  markdown: true,
  codeHighlighting: true,
  
  // Exam Features
  mbeExam: true,
  meeExam: true,
  mptExam: true,
  practiceMode: true,
  timedMode: true,
  reviewMode: true,
  explanations: true,
  hints: true,
  instantFeedback: false,
  
  // Study Features
  flashcards: true,
  outlines: true,
  mindMaps: true,
  studyGroups: true,
  studySchedule: false,
  progressTracking: true,
  performanceAnalytics: true,
  
  // Community Features
  forum: true,
  comments: true,
  likes: true,
  sharing: true,
  messaging: false,
  notifications: true,
  
  // Payment Features
  subscriptions: true,
  oneTimePayments: true,
  stripe: true,
  paypal: false,
  promotions: true,
  
  // AI Features
  aiTutor: false,
  aiGrading: false,
  aiSuggestions: false,
  aiChatbot: false,
  
  // Admin Features
  adminPanel: true,
  userManagement: true,
  contentManagement: true,
  analytics: true,
  bulkOperations: true,
  
  // Beta Features
  betaFeatures: false,
  experimentalUI: false,
  newDashboard: false,
  advancedSearch: false,
  
  // Integration Features
  googleAuth: true,
  facebookAuth: false,
  appleAuth: false,
  notion: true,
  googleDrive: false,
  dropbox: false,
  
  // Security Features
  twoFactorAuth: false,
  biometricAuth: false,
  sessionManagement: true,
  auditLogging: true,
  
  // Performance Features
  lazyLoading: true,
  infiniteScroll: true,
  caching: true,
  offlineMode: false,
  pwa: false,
  
  // Developer Features
  debugMode: env.isDevelopment,
  devTools: env.isDevelopment,
  apiMocking: false,
  testMode: env.isTest,
};

// Override with environment-based features
const environmentFeatures = {
  darkMode: env.ENABLE_DARK_MODE,
  betaFeatures: env.ENABLE_BETA_FEATURES,
  analytics: env.ENABLE_ANALYTICS,
  debugMode: env.DEBUG_MODE,
};

// Merge default features with environment overrides
export const features = {
  ...defaultFeatures,
  ...environmentFeatures,
};

// Feature flag groups for easier management
export const featureGroups = {
  exams: [
    'mbeExam',
    'meeExam',
    'mptExam',
    'practiceMode',
    'timedMode',
    'reviewMode',
  ],
  
  study: [
    'flashcards',
    'outlines',
    'mindMaps',
    'studyGroups',
    'progressTracking',
    'performanceAnalytics',
  ],
  
  community: [
    'forum',
    'comments',
    'likes',
    'sharing',
    'notifications',
  ],
  
  ai: [
    'aiTutor',
    'aiGrading',
    'aiSuggestions',
    'aiChatbot',
  ],
  
  payment: [
    'subscriptions',
    'oneTimePayments',
    'stripe',
    'promotions',
  ],
  
  beta: [
    'betaFeatures',
    'experimentalUI',
    'newDashboard',
    'advancedSearch',
  ],
};

// Helper functions for feature management
export const isFeatureEnabled = (featureName) => {
  return features[featureName] === true;
};

export const areAllFeaturesEnabled = (featureNames) => {
  return featureNames.every(name => isFeatureEnabled(name));
};

export const isAnyFeatureEnabled = (featureNames) => {
  return featureNames.some(name => isFeatureEnabled(name));
};

export const isGroupEnabled = (groupName) => {
  const group = featureGroups[groupName];
  if (!group) {
    console.warn(`Feature group "${groupName}" not found`);
    return false;
  }
  return areAllFeaturesEnabled(group);
};

export const getEnabledFeatures = () => {
  return Object.entries(features)
    .filter(([_, enabled]) => enabled)
    .map(([name]) => name);
};

export const getDisabledFeatures = () => {
  return Object.entries(features)
    .filter(([_, enabled]) => !enabled)
    .map(([name]) => name);
};

// Feature flag toggles (for development/testing)
export const toggleFeature = (featureName) => {
  if (env.isDevelopment) {
    features[featureName] = !features[featureName];
  } else {
    console.warn('Feature toggling is only available in development mode');
  }
};

export const enableFeature = (featureName) => {
  if (env.isDevelopment) {
    features[featureName] = true;
  }
};

export const disableFeature = (featureName) => {
  if (env.isDevelopment) {
    features[featureName] = false;
  }
};

// Feature flag persistence (for user preferences)
export const saveFeaturePreferences = () => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('bll_pro_feature_preferences', JSON.stringify(features));
  }
};

export const loadFeaturePreferences = () => {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('bll_pro_feature_preferences');
    if (saved) {
      try {
        const preferences = JSON.parse(saved);
        Object.assign(features, preferences);
      } catch (error) {
        console.error('Failed to load feature preferences:', error);
      }
    }
  }
};

// Feature flag validation
export const validateFeatures = () => {
  const requiredFeatures = ['mbeExam', 'meeExam', 'mptExam'];
  const missing = requiredFeatures.filter(name => !features.hasOwnProperty(name));
  
  if (missing.length > 0) {
    console.error('Missing required feature flags:', missing);
    return false;
  }
  
  return true;
};

// Log feature status in development
if (env.isDevelopment && env.DEBUG_MODE) {
  // Log specific feature groups
  Object.entries(featureGroups).forEach(([group, featureList]) => {
    const enabledInGroup = featureList.filter(f => features[f]);
  });
}